import { useState, useEffect, useContext, createContext, createElement } from 'react';
// Breakpoints aligned to AONE design spec:
//   mobile  <768
//   tablet  768–1199
//   desktop ≥1200
const TABLET_MAX = 1199;

// An explicit width, for renderers that are not the window.
//
// Every responsive decision in the app reads window.innerWidth, so a component
// drawn inside a 390px frame on a 1440px screen still believes it is on a
// desktop. That made the preview gallery lie: every "(app)" screen in it
// rendered the desktop module inside a phone-shaped box, which is not a
// rendering bug you can see and dismiss — it is the gallery showing the wrong
// thing while looking plausible.
//
// Wrapping a subtree in <ViewportWidth value={390}> makes everything inside it
// answer for that width instead. The app itself never provides one, so the
// window stays the source of truth in production.
const ViewportWidthContext = createContext(null);

// createElement rather than JSX so this stays a .js file — every one of the
// ~100 call sites imports it without an extension.
export function ViewportWidth({ value, children }) {
  return createElement(ViewportWidthContext.Provider, { value }, children);
}

export function useResponsive() {
  const forced = useContext(ViewportWidthContext);
  const [winWidth, setWinWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const h = () => setWinWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const width = forced ?? winWidth;
  return {
    isMobile: width < 768,
    isTablet: width >= 768 && width <= TABLET_MAX,
    isDesktop: width >= 1200,
    width,
  };
}
